import { useApolloClient } from "@apollo/client";
import { Spacer } from "@hackthenorth/north";
import { Button, Text } from "north.js";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { useSendSlackSponsorshipUpdateMutation } from "src/api/sponsors/sendSlackSponsorshipUpdate.generated";
import { Header, PageWrapper } from "src/shared/components";
import { errorToast } from "src/shared/components/Toast";
import { useSponsorContext } from "src/shared/contexts";
import { usePerkState } from "src/views/sponsor/perks/components/Perk/usePerkState";
import {
  GetSponsorPerksDocument,
  GetSponsorPerksQuery,
  GetSponsorPerksQueryVariables,
} from "src/views/sponsor/perks/SponsorPerksContext/graphql/getSponsorPerks.generated";
import {
  SponsorPerkType,
  CURRENT_YEAR_SPONSOR_PERKS_FIELD_KEY,
} from "src/views/sponsor/perks/types";

import { ShipmentModal } from "./components/ShipmentModal";
import { ShipmentsTable } from "./components/ShipmentsTable";
import { ShippingAddressForm } from "./components/ShippingAddressForm";
import {
  formatShipmentSlackMessage,
  parseShipmentsData,
  shipmentsEqual,
} from "./utils";

const SponsorShippedSwagPage = () => {
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState<
    number | null
  >(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { company } = useSponsorContext();
  const client = useApolloClient();
  const [sendSlackSponsorshipUpdate] = useSendSlackSponsorshipUpdateMutation();

  const { getState, updateState, isReadOnly } = usePerkState({
    perk: SponsorPerkType.SHIPPED_SWAG,
  });

  const shipmentsJson = getState("shipped_swag_shipments_json");
  const shipmentsData = useMemo(
    () => parseShipmentsData(shipmentsJson),
    [shipmentsJson]
  );

  // Reads the latest shipments version straight from the server for the
  // conflict check. Uses fetchPolicy "no-cache" so it does not reset local perk
  // state and overwrite the change we're trying to save.
  const getLatestVersion = async () => {
    if (!company) return shipmentsData.version;

    const result = await client.query<
      GetSponsorPerksQuery,
      GetSponsorPerksQueryVariables
    >({
      query: GetSponsorPerksDocument,
      variables: { sponsor_company_id: company.id.toString() },
      fetchPolicy: "no-cache",
    });

    const answers =
      result?.data?.claims[0]?.[CURRENT_YEAR_SPONSOR_PERKS_FIELD_KEY]?.answers;
    return parseShipmentsData(
      answers?.shipped_swag_shipments_json?.value ?? null
    ).version;
  };

  const isModalOpen = selectedShipmentIndex !== null || isCreateModalOpen;
  const modalShipment =
    selectedShipmentIndex !== null
      ? shipmentsData.shipments[selectedShipmentIndex]
      : undefined;

  const closeModal = () => {
    setSelectedShipmentIndex(null);
    setIsCreateModalOpen(false);
  };

  return (
    <>
      <Header
        title="Shipped Swag"
        subtitle="Track shipments of swag being sent to Hack the North."
      />
      <PageWrapper>
        <Text>
          We&apos;d love to distribute your company swag to our hackers! Use
          this page to create and track shipments of boxes you send to Hack the
          North. Click &quot;Edit&quot; on any row to view full shipment
          details.
        </Text>
        <Spacer height={8} />
        <Text>
          Please notify your sponsor coordinator via email once you&apos;ve sent
          the swag out along with the anticipated arrival date! (Note: this is
          only for swag that you want to include in the swag bags that hackers
          receive upon registration - for swag that you&apos;d like to hand out
          yourself at your sponsor booth throughout the weekend, you may choose
          to bring that yourself)
        </Text>
        <Spacer height={16} />
        <ShippingAddressForm />
        <Spacer height={50} />
        <TableHeader>
          {!isReadOnly && (
            <Button
              color="primary"
              size="md"
              onClick={() => setIsCreateModalOpen(true)}
            >
              New shipment
            </Button>
          )}
        </TableHeader>
        <Spacer height={12} />
        <ShipmentsTable
          shipments={shipmentsData.shipments}
          onEditClick={setSelectedShipmentIndex}
        />
        <Spacer height={16} />
      </PageWrapper>

      <ShipmentModal
        isOpen={isModalOpen}
        shipment={modalShipment}
        onClose={closeModal}
        onSave={async (saved) => {
          const versionSnapshot = shipmentsData.version;

          const latestVersion = await getLatestVersion();

          if (latestVersion !== versionSnapshot) {
            errorToast(
              "This shipment was updated while you were editing. Please make note of your changes, refresh the page, and try again."
            );
            return;
          }

          const updatedShipments =
            selectedShipmentIndex !== null
              ? shipmentsData.shipments.map((shipment, index) =>
                  index === selectedShipmentIndex ? saved : shipment
                )
              : [...shipmentsData.shipments, saved];

          updateState(
            "shipped_swag_shipments_json",
            JSON.stringify({
              version: latestVersion + 1,
              shipments: updatedShipments,
            })
          );

          const driName = company?.dri_user?.name ?? "Unknown DRI";
          const companyName = company?.name ?? "Unknown company";
          const isNewShipment = selectedShipmentIndex === null;
          const previousShipment =
            selectedShipmentIndex !== null
              ? shipmentsData.shipments[selectedShipmentIndex]
              : null;

          if (!previousShipment || !shipmentsEqual(previousShipment, saved)) {
            try {
              await sendSlackSponsorshipUpdate({
                variables: {
                  message: formatShipmentSlackMessage(
                    driName,
                    companyName,
                    saved,
                    isNewShipment
                  ),
                },
              });
            } catch (e) {
              console.error("Error sending sponsorship Slack notification:", e);
            }
          }

          closeModal();
        }}
      />
    </>
  );
};

export default SponsorShippedSwagPage;

const TableHeader = styled.div`
  display: flex;
  justify-content: flex-end;
`;
